import { displayDateUS } from "../../../../utils/dateUtils";

/**
 * Printable Route Sheet HTML — mirrors on-screen General Forms layout
 * (company logo, employee/patient block, visit entries with canvas signatures).
 */

const escapeHtml = (value) => {
  if (value == null) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const formatTimeWithAMPM = (timeString) => {
  if (!timeString) return "";
  try {
    const [hours, minutes] = String(timeString).split(":");
    if (hours == null || minutes == null) return String(timeString);
    const hour24 = parseInt(hours, 10);
    if (Number.isNaN(hour24)) return String(timeString);
    const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
    const ampm = hour24 >= 12 ? "PM" : "AM";
    return `${hour12}:${String(minutes).padStart(2, "0")} ${ampm}`;
  } catch {
    return String(timeString);
  }
};

const ROUTE_SHEET_PRINT_STYLES = `
  @media print {
    @page { margin: 0.4in; size: letter portrait; }
    body { margin: 0; padding: 0; }
  }
  * { box-sizing: border-box; }
  .rs-print {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 10pt;
    line-height: 1.35;
    color: #0f172a;
    padding: 8px 4px;
  }
  .rs-print .sheet-header {
    display: flex;
    align-items: center;
    gap: 16px;
    padding-bottom: 12px;
    margin-bottom: 14px;
    border-bottom: 2px solid #c7d2fe;
  }
  .rs-print .sheet-header img.logo {
    height: 56px;
    width: auto;
    max-width: 140px;
    object-fit: contain;
  }
  .rs-print .sheet-header .title-block h1 {
    margin: 0;
    font-size: 18pt;
    font-weight: 700;
    letter-spacing: 0.04em;
    color: #0f172a;
    text-transform: uppercase;
  }
  .rs-print .sheet-header .title-block p {
    margin: 2px 0 0;
    font-size: 9pt;
    color: #4f46e5;
    font-weight: 600;
  }
  .rs-print .section {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    overflow: hidden;
    margin-bottom: 12px;
  }
  .rs-print .section-head {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    background: #eef2ff;
    border-bottom: 1px solid #e0e7ff;
    font-size: 8.5pt;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #3730a3;
  }
  .rs-print .section-body { padding: 10px; }
  .rs-print .info-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px 16px;
  }
  .rs-print .field-label {
    display: block;
    font-size: 7.5pt;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 2px;
  }
  .rs-print .field-value {
    min-height: 18px;
    padding: 3px 0;
    border-bottom: 1px solid #cbd5e1;
    font-size: 10pt;
    font-weight: 600;
    color: #0f172a;
  }
  .rs-print .entry-card {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    margin-bottom: 10px;
    overflow: hidden;
    page-break-inside: avoid;
  }
  .rs-print .entry-card:last-child { margin-bottom: 0; }
  .rs-print .entry-head {
    padding: 5px 10px;
    background: #eef2ff;
    border-bottom: 1px solid #e0e7ff;
    font-size: 8pt;
    font-weight: 700;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: #3730a3;
  }
  .rs-print .entry-body { padding: 8px 10px; }
  .rs-print .meta-grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 8px;
    margin-bottom: 8px;
  }
  .rs-print .sig-box {
    border: 1px dashed #cbd5e1;
    border-radius: 6px;
    background: #f8fafc;
    min-height: 64px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px;
  }
  .rs-print .sig-box img {
    max-height: 56px;
    max-width: 100%;
    object-fit: contain;
  }
  .rs-print .sig-empty {
    font-size: 8pt;
    font-style: italic;
    color: #94a3b8;
  }
  .rs-print .codes-row {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: 10px;
    margin-top: 4px;
    font-size: 8pt;
  }
  .rs-print .codes-box {
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    padding: 8px;
  }
  .rs-print .codes-box strong {
    display: block;
    margin-bottom: 4px;
    color: #3730a3;
    font-size: 7.5pt;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }
  .rs-print .codes-box div { margin-bottom: 2px; color: #334155; }
`;

const resolveSignatureSrc = (entry) => {
  const candidates = [
    entry?.patientSignatureUrl,
    entry?.patientSignatureImage,
    entry?.patientSignatureS3Url,
    entry?.patientSignature,
  ];
  for (const raw of candidates) {
    if (!raw || typeof raw !== "string") continue;
    const v = raw.trim();
    if (
      v.startsWith("data:image") ||
      v.startsWith("http://") ||
      v.startsWith("https://") ||
      v.startsWith("/")
    ) {
      return v;
    }
  }
  return "";
};

/**
 * @param {Record<string, unknown>} routeSheet
 * @param {{ logoUrl?: string, companyName?: string }} [branding]
 * @returns {{ html: string, title: string }}
 */
export function buildRouteSheetPrintDocument(routeSheet, branding = {}) {
  const data = routeSheet || {};
  const entries = Array.isArray(data.entries) ? data.entries : [];
  const logoUrl = branding.logoUrl || "/logo.png.png";
  const companyName = branding.companyName || "";

  const employeeName = escapeHtml(data.employeeName || data.applicantName || "");
  const title = escapeHtml(data.title || "");
  const patientName = escapeHtml(data.patientName || "");
  const mrNumber = escapeHtml(data.mrNumber || "");

  const entryCards =
    entries.length === 0
      ? `<div class="entry-card"><div class="entry-body"><span class="sig-empty">No visit entries</span></div></div>`
      : entries
          .map((entry, index) => {
            const sigSrc = resolveSignatureSrc(entry);
            const sigHtml = sigSrc
              ? `<img src="${escapeHtml(sigSrc)}" alt="Patient signature" />`
              : `<span class="sig-empty">Not signed</span>`;
            return `
              <div class="entry-card">
                <div class="entry-head">Entry ${index + 1}</div>
                <div class="entry-body">
                  <div class="meta-grid">
                    <div>
                      <span class="field-label">Date</span>
                      <div class="field-value">${escapeHtml(displayDateUS(entry.visitDate) || "—")}</div>
                    </div>
                    <div>
                      <span class="field-label">Code</span>
                      <div class="field-value">${escapeHtml(entry.visitCode || "—")}</div>
                    </div>
                    <div>
                      <span class="field-label">Time In</span>
                      <div class="field-value">${escapeHtml(formatTimeWithAMPM(entry.timeIn) || "—")}</div>
                    </div>
                    <div>
                      <span class="field-label">Time Out</span>
                      <div class="field-value">${escapeHtml(formatTimeWithAMPM(entry.timeOut) || "—")}</div>
                    </div>
                    <div>
                      <span class="field-label">Mileage</span>
                      <div class="field-value">${escapeHtml(
                        entry.mileage != null && entry.mileage !== ""
                          ? String(entry.mileage)
                          : "—",
                      )}</div>
                    </div>
                  </div>
                  <span class="field-label">Patient Signature</span>
                  <div class="sig-box">${sigHtml}</div>
                </div>
              </div>
            `;
          })
          .join("");

  const body = `
    <div class="rs-print">
      <div class="sheet-header">
        <img class="logo" src="${escapeHtml(logoUrl)}" alt="Company logo" onerror="this.style.display='none'" />
        <div class="title-block">
          <h1>Route Sheet</h1>
          ${companyName ? `<p>${escapeHtml(companyName)}</p>` : ""}
        </div>
      </div>

      <div class="section">
        <div class="section-head">Employee &amp; Patient</div>
        <div class="section-body">
          <div class="info-grid">
            <div>
              <span class="field-label">Employee Name</span>
              <div class="field-value">${employeeName || "—"}</div>
            </div>
            <div>
              <span class="field-label">Title</span>
              <div class="field-value">${title || "—"}</div>
            </div>
            <div>
              <span class="field-label">Patient Name</span>
              <div class="field-value">${patientName || "—"}</div>
            </div>
            <div>
              <span class="field-label">MR Number</span>
              <div class="field-value">${mrNumber || "—"}</div>
            </div>
          </div>
        </div>
      </div>

      <div class="section">
        <div class="section-head">Visit Entries</div>
        <div class="section-body">
          ${entryCards}
        </div>
      </div>

      <div class="codes-row">
        <div class="codes-box">
          <strong>Billable visit codes</strong>
          <div><b>E</b> Initial Assessment</div>
          <div><b>RV</b> Return Visit</div>
          <div><b>IV</b> IV Infusion Visit</div>
          <div><b>RC</b> Recertification</div>
          <div><b>RE</b> Resumption of Care</div>
          <div><b>DC</b> Discharge</div>
        </div>
        <div class="codes-box">
          <strong>Non-billable visit codes</strong>
          <div><b>Ref</b> Refused Visit</div>
          <div><b>NF</b> No Answer to Knock</div>
          <div><b>S</b> Supervisory Visit</div>
        </div>
        <div class="codes-box">
          <strong>Office codes</strong>
          <div><b>O</b> Orientation</div>
          <div><b>IS</b> In-Service</div>
        </div>
      </div>
    </div>
  `;

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Route Sheet - ${employeeName || "Employee"}</title><style>${ROUTE_SHEET_PRINT_STYLES}</style></head><body>${body}</body></html>`;
  const printTitle = `Route Sheet — ${data.employeeName || data.applicantName || data.patientName || "Form"}`;
  return { html, title: printTitle };
}
